/**
 * Global Error Handler
 * 
 * Sets up handlers for:
 * - Uncaught JavaScript errors
 * - Unhandled promise rejections
 * - Resource loading errors
 * 
 * Logs everything to your server so iOS gray screen issues become visible
 */

class GlobalErrorHandler {
  constructor() {
    this.isInitialized = false;
    this.errorQueue = [];
    this.maxQueueSize = 50;
  }

  /**
   * Initialize global error handlers
   * Call this ONCE in your index.js or App.js
   */
  initialize() {
    if (this.isInitialized) {
      console.warn('GlobalErrorHandler already initialized');
      return;
    }

    console.log('🔧 Initializing GlobalErrorHandler for iOS debugging...');

    // Handle uncaught JavaScript errors
    window.addEventListener('error', this.handleError);

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleRejection);

    // Log when app becomes visible (useful for iOS PWA debugging)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Log initial load information
    this.logInitialState();

    this.isInitialized = true;
    console.log('✅ GlobalErrorHandler initialized');
  }

  /**
   * Handle uncaught JavaScript errors
   */
  handleError = (event) => {
    console.error('🔴 Uncaught Error:', event.error);

    const errorData = {
      type: 'error',
      errorId: `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      error: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack || 'No stack trace available',
        name: event.error?.name || 'Error'
      },
      context: this.getContext()
    };

    this.logToServer(errorData);

    // Don't prevent default error handling
    return false;
  };

  /**
   * Handle unhandled promise rejections
   */
  handleRejection = (event) => {
    console.error('🔴 Unhandled Promise Rejection:', event.reason);

    const errorData = {
      type: 'unhandledRejection',
      errorId: `REJ-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      error: {
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack || 'No stack trace available',
        name: event.reason?.name || 'Promise Rejection'
      },
      promise: event.promise?.toString() || 'Unknown promise',
      context: this.getContext()
    };

    this.logToServer(errorData);

    // Prevent default handling (which logs to console)
    event.preventDefault();
  };

  /**
   * Handle visibility changes (useful for iOS PWA debugging)
   */
  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      console.log('👁️ App became visible');
      this.logEvent({
        type: 'visibility',
        state: 'visible',
        timestamp: new Date().toISOString(),
        context: this.getContext()
      });
    }
  };

  /**
   * Log initial app state
   */
  logInitialState() {
    const initialState = {
      type: 'appLoad',
      timestamp: new Date().toISOString(),
      context: this.getContext(),
      performance: this.getPerformanceMetrics()
    };

    console.log('📊 Initial App State:', initialState);
    this.logToServer(initialState);
  }

  /**
   * Get context information about the environment
   */
  getContext() {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isIPad = /iPad/.test(userAgent) || 
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && window.navigator.standalone);

    // Check for common iOS issues
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotifications = 'Notification' in window;

    return {
      url: window.location.href,
      pathname: window.location.pathname,
      userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      
      // Device detection
      isIOS,
      isIPad,
      isSafari,
      isStandalone,
      isPWA: isStandalone,
      
      // Feature detection
      hasServiceWorker,
      hasPushManager,
      hasNotifications,
      
      // Viewport
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio
      },
      
      // Screen
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        orientation: window.screen.orientation?.type
      },
      
      // User info
      user: this.getUserInfo(),
      
      // Storage
      localStorage: this.checkStorage('localStorage'),
      sessionStorage: this.checkStorage('sessionStorage')
    };
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    if (!window.performance || !window.performance.timing) {
      return null;
    }

    const timing = window.performance.timing;
    const navigation = window.performance.navigation;

    return {
      // Page load time
      loadTime: timing.loadEventEnd - timing.navigationStart,
      domReady: timing.domContentLoadedEventEnd - timing.navigationStart,
      
      // Navigation timing
      navigationStart: timing.navigationStart,
      redirectTime: timing.redirectEnd - timing.redirectStart,
      domainLookupTime: timing.domainLookupEnd - timing.domainLookupStart,
      connectTime: timing.connectEnd - timing.connectStart,
      requestTime: timing.responseEnd - timing.requestStart,
      
      // Navigation type
      navigationType: navigation.type, // 0=navigate, 1=reload, 2=back/forward
      redirectCount: navigation.redirectCount
    };
  }

  /**
   * Get user information from storage
   */
  getUserInfo() {
    try {
      return {
        tenantId: localStorage.getItem('tenantId') || 'unknown',
        userId: localStorage.getItem('userId') || 'unknown',
        sessionId: sessionStorage.getItem('sessionId') || 'unknown'
      };
    } catch (e) {
      return { tenantId: 'unknown', userId: 'unknown', sessionId: 'unknown' };
    }
  }

  /**
   * Check if storage is available
   */
  checkStorage(type) {
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return { available: true, itemCount: storage.length };
    } catch (e) {
      return { available: false, error: e.message };
    }
  }

  /**
   * Log event to server
   */
  logEvent(eventData) {
    console.log('📝 Logging event:', eventData.type);
    this.logToServer(eventData);
  }

  /**
   * Send error/event data to server
   */
  async logToServer(data) {
    // Add to queue
    this.errorQueue.push(data);
    
    // Keep queue size manageable
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      
      const response = await fetch(`${baseUrl}/api/errors/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        keepalive: true // Ensures request completes even if page unloads
      });

      if (response.ok) {
        console.log('✅ Error logged to server:', data.errorId || data.type);
      } else {
        console.error('❌ Server returned error:', response.status);
      }
    } catch (err) {
      console.error('❌ Failed to log to server:', err);
      // Store in localStorage as fallback
      this.storeInLocalStorage(data);
    }
  }

  /**
   * Store error in localStorage as fallback
   */
  storeInLocalStorage(data) {
    try {
      const key = `error_log_${Date.now()}`;
      localStorage.setItem(key, JSON.stringify(data));
      console.log('💾 Error stored in localStorage:', key);
      
      // Clean up old errors (keep last 10)
      const errorKeys = Object.keys(localStorage)
        .filter(k => k.startsWith('error_log_'))
        .sort();
      
      while (errorKeys.length > 10) {
        const oldestKey = errorKeys.shift();
        localStorage.removeItem(oldestKey);
      }
    } catch (e) {
      console.error('Failed to store error in localStorage:', e);
    }
  }

  /**
   * Get error queue (useful for debugging)
   */
  getErrorQueue() {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue() {
    this.errorQueue = [];
  }

  /**
   * Manually log an error
   */
  logError(error, context = {}) {
    const errorData = {
      type: 'manual',
      errorId: `MAN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      error: {
        message: error.message || String(error),
        stack: error.stack || 'No stack trace',
        name: error.name || 'Error'
      },
      context: {
        ...this.getContext(),
        ...context
      }
    };

    console.error('🔴 Manual Error Log:', errorData);
    this.logToServer(errorData);
  }

  /**
   * Clean up handlers (call on app unmount if needed)
   */
  cleanup() {
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleRejection);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.isInitialized = false;
    console.log('🧹 GlobalErrorHandler cleaned up');
  }
}

// Create singleton instance
const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;